<template>
  <div class="min-h-screen p-4 md:p-6 lg:p-8">
    <Banner />
    <!-- Header Section -->
    <div class="mb-8">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-[15px] sm:text-3xl font-bold text-gray-900">
            Teacher Attendance Dashboard
          </h1>
        </div>
        <div class="flex items-center space-x-4">
          <!-- Notifications Bell -->
          <div class="relative">
            <button
              @click="showNotifications = !showNotifications"
              class="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
            >
              <Bell class="h-6 w-6" />
              <span
                v-if="notifications.length > 0"
                class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
              >
                {{ notifications.length }}
              </span>
            </button>

            <!-- Enhanced Notifications Dropdown -->
            <div
              v-if="showNotifications"
              class="absolute right-[-28px] sm:right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border z-50"
            >
              <div class="p-4 border-b bg-gray-50 rounded-t-lg">
                <div class="flex items-center justify-between">
                  <h3 class="font-semibold text-gray-900">Notifications</h3>
                  <span class="text-xs text-gray-500"
                    >{{ notifications.length }} items</span
                  >
                </div>
              </div>
              <div class="max-h-80 overflow-y-auto">
                <div
                  v-if="isLoadingNotifications"
                  class="p-6 text-center"
                >
                  <div
                    class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"
                  ></div>
                  <p class="text-sm text-gray-500">Loading notifications...</p>
                </div>
                <div
                  v-else-if="notifications.length === 0"
                  class="p-6 text-gray-500 text-center"
                >
                  <Bell class="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p class="text-sm">No new notifications</p>
                </div>
                <div
                  v-else
                  class="divide-y divide-gray-100"
                >
                  <div
                    v-for="notification in notifications"
                    :key="notification.id || notification"
                    class="p-4 hover:bg-gray-50 transition-colors duration-150"
                  >
                    <div class="flex items-start space-x-3">
                      <!-- Notification Icon -->
                      <div
                        :class="[
                          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
                          getNotificationIconClass(notification),
                        ]"
                      >
                        <component
                          :is="getNotificationIcon(notification)"
                          class="w-4 h-4"
                        />
                      </div>

                      <!-- Notification Content -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center justify-between">
                          <p
                            class="text-xs font-medium text-gray-600 uppercase tracking-wide"
                          >
                            {{ getNotificationTitle(notification) }}
                          </p>
                          <span
                            :class="[
                              'text-xs px-2 py-1 rounded-full',
                              getNotificationSeverityClass(notification),
                            ]"
                          >
                            {{ getNotificationSeverity(notification) }}
                          </span>
                        </div>
                        <p class="text-sm text-gray-900 mt-1">
                          {{ getNotificationMessage(notification) }}
                        </p>
                        <p class="text-xs text-gray-500 mt-1">
                          {{ formatNotificationTime(notification) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notification Footer -->
              <div class="p-3 border-t bg-gray-50 rounded-b-lg">
                <button
                  @click="refreshNotifications"
                  :disabled="isLoadingNotifications"
                  class="w-full text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                >
                  <RefreshCw
                    class="w-3 h-3 inline mr-1"
                    :class="{ 'animate-spin': isLoadingNotifications }"
                  />
                  Refresh Notifications
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div class="bg-white rounded-lg shadow p-3">
        <div class="flex items-center">
          <div class="p-1.5 bg-blue-100 rounded-lg">
            <Users class="h-5 w-5 text-blue-600" />
          </div>
          <div class="ml-3">
            <p class="text-xs font-medium text-gray-600">Total Recoards</p>
            <p class="text-lg font-bold text-gray-900">
              {{ summaryStats.total }}
            </p>
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow p-3">
        <div class="flex items-center">
          <div class="p-1.5 bg-green-100 rounded-lg">
            <UserCheck class="h-5 w-5 text-green-600" />
          </div>
          <div class="ml-3">
            <p class="text-xs font-medium text-gray-600">Present</p>
            <p class="text-lg font-bold text-gray-900">
              {{ summaryStats.present }}
            </p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-3">
        <div class="flex items-center">
          <div class="p-1.5 bg-red-100 rounded-lg">
            <UserX class="h-5 w-5 text-red-600" />
          </div>
          <div class="ml-3">
            <p class="text-xs font-medium text-gray-600">Absent</p>
            <p class="text-lg font-bold text-gray-900">
              {{ summaryStats.absent }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Attendance Range Filters Section -->

    <!-- Filters and Search -->
    <div class="bg-white rounded-lg shadow p-6 mb-8">
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0"
      >
        <div
          class="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4"
        >
          <div class="relative">
            <Search
              class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4"
            />
            <input
              v-model="searchName"
              type="text"
              placeholder="Search by teacher name..."
              class="w-full max-w-xs pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              @input="debouncedSearch"
            />
          </div>
        </div>
        <div>
          <div class="flex flex-col md:flex-row md:items-center gap-4">
            <div class="flex items-center gap-4">
              <label class="text-sm font-medium text-gray-700"
                >Attendance Range:</label
              >
              <select
                v-model="attendanceRange"
                @change="handleRangeChange"
                class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7days">Last 7 Days</option>
                <option value="custom">Custom Range</option>
              </select>

              <!-- Refresh button positioned to the right of dropdown -->
              <button
                @click="fetchAttendanceData"
                class="px-3 py-2 sm:px-4 sm:py-2 text-blue-600 sm:text-white sm:bg-blue-600 sm:hover:bg-blue-700 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <svg
                  class="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span class="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          <!-- Custom Date Range Inputs -->
          <div
            v-if="attendanceRange === 'custom'"
            class="flex gap-4 mt-4"
          >
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1"
                >From</label
              >
              <input
                v-model="customDateFrom"
                type="date"
                class="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @change="fetchAttendanceData"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1"
                >To</label
              >
              <input
                v-model="customDateTo"
                type="date"
                class="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @change="fetchAttendanceData"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Daily Attendance Breakdown Table (visible when Last 7 Days or Custom Range is selected) -->
    <div
      v-if="
        (attendanceRange === 'last7days' || attendanceRange === 'custom') &&
        attendanceRecords.length > 0
      "
      class="bg-white rounded-lg shadow p-6 mb-8"
    >
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900">
          Daily Attendance Breakdown
        </h3>
        <span class="text-sm text-gray-600">
          Selected Period: <strong>{{ attendanceRangeLabel }}</strong>
        </span>
      </div>

      <!-- Daily Breakdown Table -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Teacher
              </th>
              <th
                v-for="date in dateRangeArray"
                :key="date"
                class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {{ formatDateHeader(date) }}
              </th>
              <th
                v-if="
                  attendanceRange !== 'today' && attendanceRange !== 'yesterday'
                "
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Present Days
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="record in groupedAttendanceByTeacher"
              :key="record._id"
              class="hover:bg-gray-50"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div
                    class="h-8 w-8 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 font-semibold text-xs overflow-hidden"
                  >
                    <img
                      v-if="getTeacherPhoto(record)"
                      :src="getTeacherPhoto(record)"
                      :alt="getTeacherName(record)"
                      class="h-full w-full object-cover"
                    />
                    <span v-else>
                      {{ getInitials(getTeacherName(record)) }}
                    </span>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900">
                      {{ getTeacherName(record) }}
                    </div>
                    <div class="text-sm text-gray-500">
                      ID: {{ getTeacherId(record) }}
                    </div>
                  </div>
                </div>
              </td>
              <td
                v-for="date in dateRangeArray"
                :key="`${record._id}-${date}`"
                class="px-4 py-4 text-center"
              >
                <div class="flex flex-col items-center space-y-1">
                  <span
                    :class="[
                      'inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold',
                      getAttendanceStatusForDate(record, date) === 'present'
                        ? 'bg-green-100 text-green-800'
                        : getAttendanceStatusForDate(record, date) === 'absent'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800',
                    ]"
                  >
                    {{
                      getAttendanceStatusForDate(record, date) === "present"
                        ? "P"
                        : getAttendanceStatusForDate(record, date) === "absent"
                        ? "A"
                        : "-"
                    }}
                  </span>
                  <span
                    v-if="getCheckInCheckOutTime(record, date)"
                    class="text-xs text-gray-600 whitespace-nowrap"
                  >
                    {{ getCheckInCheckOutTime(record, date) }}
                  </span>
                </div>
              </td>
              <td class="px-6 py-4 text-center">
                <div class="flex items-center justify-center space-x-2">
                  <span class="text-sm font-semibold text-blue-600">
                    {{ calculatePresentDaysForTeacher(record) }}
                  </span>
                  <span class="text-sm text-gray-500">
                    / {{ totalDaysInRange }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Legend -->
      <div class="mt-4 flex items-center justify-start space-x-6">
        <div class="flex items-center space-x-2">
          <span
            class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-800 text-xs font-bold"
          >
            P
          </span>
          <span class="text-sm text-gray-600">Present</span>
        </div>
        <div class="flex items-center space-x-2">
          <span
            class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-800 text-xs font-bold"
          >
            A
          </span>
          <span class="text-sm text-gray-600">Absent</span>
        </div>
        <div class="flex items-center space-x-2">
          <span
            class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-800 text-xs font-bold"
          >
            -
          </span>
          <span class="text-sm text-gray-600">No Data</span>
        </div>
      </div>
    </div>

    <!-- Attendance Table/Cards -->
    <div
      v-if="!(attendanceRange === 'last7days' || attendanceRange === 'custom')"
      class="bg-white rounded-lg shadow overflow-hidden"
    >
      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">
          Teacher Attendance - {{ attendanceRangeLabel }}
        </h2>
      </div>

      <div
        v-if="isTableLoading"
        class="flex items-center justify-center py-12"
      >
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
        ></div>
        <span class="ml-2 text-gray-600">Loading attendance data...</span>
      </div>

      <div
        v-else-if="attendanceRecords.length === 0"
        class="text-center py-12"
      >
        <Users class="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          No attendance records found
        </h3>
        <p class="text-gray-500 mb-4">
          {{
            searchName
              ? "Try adjusting your search terms or filters."
              : "No attendance data available for the selected range."
          }}
        </p>
        <button
          @click="fetchAttendanceData(1)"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Refresh Data
        </button>
      </div>

      <div v-else>
        <!-- Mobile Cards View -->
        <div class="block md:hidden">
          <div class="divide-y divide-gray-200">
            <div
              v-for="record in attendanceRecords"
              :key="record._id"
              class="p-4 hover:bg-gray-50 transition-colors duration-150"
            >
              <div class="flex items-start space-x-3">
                <div
                  class="h-10 w-10 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 font-semibold text-sm overflow-hidden flex-shrink-0"
                >
                  <img
                    v-if="getTeacherPhoto(record)"
                    :src="getTeacherPhoto(record)"
                    :alt="getTeacherName(record)"
                    class="h-full w-full object-cover"
                  />
                  <span v-else>
                    {{ getInitials(getTeacherName(record)) }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between mb-2">
                    <h3 class="text-sm font-medium text-gray-900 truncate">
                      {{ getTeacherName(record) }}
                    </h3>
                    <span
                      :class="getStatusBadgeClass(record.status)"
                      class="inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize"
                    >
                      {{ record.status }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 mb-2">
                    ID: {{ getTeacherId(record) }}
                  </p>
                  <div class="grid grid-cols-2 gap-2 text-xs mb-2">
                    <div>
                      <span class="text-gray-500">Check In:</span>
                      <p class="font-medium text-gray-900">
                        {{ formatTime(record.checkIn) }}
                      </p>
                    </div>
                    <div>
                      <span class="text-gray-500">Check Out:</span>
                      <p class="font-medium text-gray-900">
                        <span v-if="record.checkOut">
                          {{ formatTime(record.checkOut) }}
                        </span>
                        <span
                          v-else
                          class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-800 text-xs font-bold"
                        >
                          -
                        </span>
                      </p>
                    </div>
                  </div>
                  <div class="mb-2">
                    <span class="text-xs text-gray-500">Duration:</span>
                    <p class="text-xs font-medium text-gray-900">
                      <span
                        v-if="
                          calculateDuration(record.checkIn, record.checkOut)
                        "
                      >
                        {{ calculateDuration(record.checkIn, record.checkOut) }}
                      </span>
                      <span
                        v-else
                        class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-800 text-xs font-bold"
                      >
                        -
                      </span>
                    </p>
                  </div>
                  <!-- Present Days Count for Mobile -->
                  <div
                    v-if="
                      attendanceRange !== 'today' &&
                      attendanceRange !== 'yesterday'
                    "
                    class="bg-blue-50 px-2 py-1 rounded"
                  >
                    <span class="text-xs text-blue-700 font-semibold">
                      Present: {{ getPresenceCount(record) }} /
                      {{ totalDaysInRange }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Table View -->
        <div class="hidden md:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Teacher
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Check In
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Check Out
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Duration
                </th>
                <!-- New Column for Presence Count -->
                <th
                  v-if="
                    attendanceRange !== 'today' &&
                    attendanceRange !== 'yesterday'
                  "
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Present Days
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="record in attendanceRecords"
                :key="record._id"
                class="hover:bg-gray-50"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div
                      class="h-8 w-8 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 font-semibold text-xs overflow-hidden"
                    >
                      <img
                        v-if="getTeacherPhoto(record)"
                        :src="getTeacherPhoto(record)"
                        :alt="getTeacherName(record)"
                        class="h-full w-full object-cover"
                      />
                      <span v-else>
                        {{ getInitials(getTeacherName(record)) }}
                      </span>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">
                        {{ getTeacherName(record) }}
                      </div>
                      <div class="text-sm text-gray-500">
                        ID: {{ getTeacherId(record) }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="getStatusBadgeClass(record.status)"
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize"
                  >
                    {{ record.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatTime(record.checkIn) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span v-if="record.checkOut">
                    {{ formatTime(record.checkOut) }}
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-800 text-xs font-bold ml-[20px]"
                  >
                    -
                  </span>
                </td>

                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span
                    v-if="calculateDuration(record.checkIn, record.checkOut)"
                  >
                    {{ calculateDuration(record.checkIn, record.checkOut) }}
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-800 text-xs font-bold"
                  >
                    -
                  </span>
                </td>

                <!-- New Column Showing Present Days in Selected Range -->
                <td
                  v-if="
                    attendanceRange !== 'today' &&
                    attendanceRange !== 'yesterday'
                  "
                  class="px-6 py-4 whitespace-nowrap"
                >
                  <div class="flex items-center space-x-2">
                    <span class="text-sm font-semibold text-blue-600">
                      {{ getPresenceCount(record) }}
                    </span>
                    <span class="text-sm text-gray-500">
                      / {{ totalDaysInRange }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div
        v-if="attendanceRecords.length > 0"
        class="bg-gray-50 px-4 md:px-6 py-3 flex items-center justify-between border-t border-gray-200"
      >
        <div class="flex-1 flex justify-between sm:hidden">
          <button
            @click="previousPage"
            :disabled="currentPage <= 1"
            class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            @click="nextPage"
            :disabled="currentPage >= totalPages"
            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div
          class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
        >
          <div>
            <p class="text-sm text-gray-700">
              Showing
              <span class="font-medium">{{ getStartRecord() }}</span>
              to
              <span class="font-medium">{{ getEndRecord() }}</span>
              of
              <span class="font-medium">{{ attendanceTotal }}</span>
              results
            </p>
          </div>
          <div>
            <nav
              class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
            >
              <button
                @click="previousPage"
                :disabled="currentPage <= 1"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft class="h-5 w-5" />
              </button>

              <button
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                  page === currentPage
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                ]"
              >
                {{ page }}
              </button>

              <button
                @click="nextPage"
                :disabled="currentPage >= totalPages"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight class="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import {
    Bell,
    UserCheck,
    UserX,
    Clock,
    Users,
    Search,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Info,
  } from "lucide-vue-next";
  import Banner from "@/components/Banner/TeacherBanner.vue";
  const store = useStore();
  // 🔁 Auto Refresh
  const attendanceInterval = ref(null);
  const notificationInterval = ref(null);
  // Reactive data
  const showNotifications = ref(false);
  const searchName = ref("");
  const selectedDate = ref(new Date().toISOString().split("T")[0]);
  const pageLimit = ref(10);
  const currentPage = ref(1);
  const isTableLoading = ref(false);
  const isLoadingNotifications = ref(false);

  // Attendance Range State Management
  const attendanceRange = ref("today");
  const customDateFrom = ref("");
  const customDateTo = ref("");

  // Computed properties from Vuex store
  const attendanceRecords = computed(() => {
    return store.state.attendanceRecords || [];
  });

  const attendanceTotal = computed(() => {
    return store.state.attendanceTotal || 0;
  });

  const notifications = computed(() => {
    const rawNotifications = store.state.notifications || [];

    // Handle both string array and object array formats
    return rawNotifications.map((notification, index) => {
      if (typeof notification === "string") {
        return {
          id: `notification_${index}`,
          message: notification,
          type: "info",
          severity: "info",
          createdAt: new Date().toISOString(),
          title: "Attendance Alert",
        };
      }
      return notification;
    });
  });

  // Computed stats based on actual data
  const summaryStats = computed(() => {
    const records = attendanceRecords.value;
    const total = records.length;
    const present = records.filter((r) => r.status === "present").length;
    const late = records.filter((r) => r.status === "late").length;
    const absent = records.filter((r) => r.status === "absent").length;

    return { total, present, late, absent };
  });

  // Computed Attendance Range Label
  const attendanceRangeLabel = computed(() => {
    switch (attendanceRange.value) {
      case "yesterday":
        // eslint-disable-next-line no-case-declarations
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toLocaleDateString("en-US", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      case "last7days":
        // eslint-disable-next-line no-case-declarations
        const from = new Date();
        from.setDate(from.getDate() - 6);
        // eslint-disable-next-line no-case-declarations
        const to = new Date();
        return `${from.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} - ${to.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })}`;
      case "custom":
        return `${customDateFrom.value} to ${customDateTo.value}`;
      default:
        return new Date().toLocaleDateString("en-US", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        });
    }
  });

  // Computed Total Days in Selected Range
  const totalDaysInRange = computed(() => {
    if (attendanceRange.value === "yesterday") return 1;
    if (attendanceRange.value === "last7days") return 7;
    if (
      attendanceRange.value === "custom" &&
      customDateFrom.value &&
      customDateTo.value
    ) {
      const from = new Date(customDateFrom.value);
      const to = new Date(customDateTo.value);
      const diffTime = Math.abs(to - from);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 1;
  });

  const dateRangeArray = computed(() => {
    const dates = [];
    let startDate, endDate;

    if (attendanceRange.value === "last7days") {
      endDate = new Date();
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 6);
    } else if (attendanceRange.value === "custom") {
      startDate = new Date(customDateFrom.value);
      endDate = new Date(customDateTo.value);
    } else {
      return dates;
    }

    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split("T")[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  });

  // Computed Pagination
  const totalPages = computed(() =>
    Math.ceil(attendanceTotal.value / pageLimit.value),
  );
  const visiblePages = computed(() => {
    const pages = [];
    const start = Math.max(1, currentPage.value - 2);
    const end = Math.min(totalPages.value, currentPage.value + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  });

  // Helper Methods to Extract Teacher Data
  const getTeacherName = (record) => {
    return record.userId?.name || record.name || "Unknown Teacher";
  };

  const getTeacherPhoto = (record) => {
    return record.userId?.photo || record.photo || null;
  };

  const getTeacherId = (record) => {
    return (
      record.userId?.attendanceID ||
      record.attendanceID ||
      record.attendanceID ||
      "N/A"
    );
  };

  // New Method to Calculate Presence Count in Range
  const getPresenceCount = (record) => {
    // If you have a presentDays field in your record, return it
    // Otherwise, this can be enhanced based on your data structure
    return record.presentDays || record.presentCount || 0;
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
    });
  };

  const getAttendanceStatusForDate = (record, dateString) => {
    if (!record.dailyAttendance || !Array.isArray(record.dailyAttendance)) {
      return "no-data";
    }

    const dailyRecord = record.dailyAttendance.find(
      (day) => day.date === dateString,
    );

    if (!dailyRecord) return "no-data";

    const status = dailyRecord.status?.toLowerCase();
    if (status === "present" || status === "p") return "present";
    if (status === "absent" || status === "a") return "absent";

    return "no-data";
  };

  const calculatePresentDaysForTeacher = (record) => {
    if (!record.dailyAttendance || !Array.isArray(record.dailyAttendance)) {
      return 0;
    }

    return record.dailyAttendance.filter((day) => {
      const status = day.status?.toLowerCase();
      return status === "present" || status === "p";
    }).length;
  };

  const getCheckInCheckOutTime = (record, dateString) => {
    if (!record.dailyAttendance || !Array.isArray(record.dailyAttendance)) {
      return null;
    }

    const dailyRecord = record.dailyAttendance.find(
      (day) => day.date === dateString,
    );

    if (!dailyRecord || !dailyRecord.checkIn) {
      return null; // no check-in → show nothing
    }

    const checkInTime = formatTime(dailyRecord.checkIn);

    // if checkout exists, show range
    if (dailyRecord.checkOut) {
      const checkOutTime = formatTime(dailyRecord.checkOut);
      return `${checkInTime} - ${checkOutTime}`;
    }

    // only check-in exists
    return checkInTime;
  };

  // Notification Helper Methods
  const getNotificationMessage = (notification) => {
    return notification.message || notification;
  };

  const getNotificationTitle = (notification) => {
    return notification.title || "Notification";
  };

  const getNotificationSeverity = (notification) => {
    return notification.severity || "info";
  };

  const getNotificationIcon = (notification) => {
    const severity = getNotificationSeverity(notification);
    switch (severity) {
      case "error":
        return AlertCircle;
      case "warning":
        return Clock;
      case "info":
        return Info;
      default:
        return CheckCircle;
    }
  };

  const getNotificationIconClass = (notification) => {
    const severity = getNotificationSeverity(notification);
    switch (severity) {
      case "error":
        return "bg-red-100 text-red-600";
      case "warning":
        return "bg-yellow-100 text-yellow-600";
      case "info":
        return "bg-blue-100 text-blue-600";
      default:
        return "bg-green-100 text-green-600";
    }
  };

  const getNotificationSeverityClass = (notification) => {
    const severity = getNotificationSeverity(notification);
    switch (severity) {
      case "error":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "info":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-green-100 text-green-800";
    }
  };

  const formatNotificationTime = (notification) => {
    const createdAt = notification.createdAt || new Date().toISOString();
    try {
      const date = new Date(createdAt);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Recently";
    }
  };

  // Methods

  // New Method to Set Attendance Range
  // eslint-disable-next-line no-unused-vars
  const setAttendanceRange = (range) => {
    attendanceRange.value = range;
    if (range === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      selectedDate.value = yesterday.toISOString().split("T")[0];
    } else if (range === "last7days") {
      const from = new Date();
      from.setDate(from.getDate() - 6);
      customDateFrom.value = from.toISOString().split("T")[0];
      customDateTo.value = new Date().toISOString().split("T")[0];
    } else if (range === "custom") {
      // User will set custom dates
    } else {
      selectedDate.value = new Date().toISOString().split("T")[0];
    }
    currentPage.value = 1;
    fetchAttendanceData();
  };

  const fetchAttendanceData = async (
    page = currentPage.value,
    silent = false,
  ) => {
    if (!silent) {
      isTableLoading.value = true;
    }

    try {
      currentPage.value = Number(page);
      const limit = Number(pageLimit.value);

      const payload = {
        page: currentPage.value,
        limit: limit,
        name: searchName.value.trim(),
        range: attendanceRange.value,
      };

      if (attendanceRange.value === "custom") {
        payload.dateFrom = customDateFrom.value;
        payload.dateTo = customDateTo.value;
      } else if (attendanceRange.value === "yesterday") {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        payload.date = yesterday.toISOString().split("T")[0];
      } else if (attendanceRange.value === "last7days") {
        payload.dateFrom = customDateFrom.value;
        payload.dateTo = customDateTo.value;
      } else {
        payload.date = selectedDate.value;
      }

      await store.dispatch("fetchAttendanceForToday", payload);
    } catch (error) {
      console.error("❌ Error fetching attendance data:", error);
    } finally {
      if (!silent) {
        isTableLoading.value = false;
      }
    }
  };

  const fetchNotifications = async (silent = false) => {
    if (!silent) {
      isLoadingNotifications.value = true;
    }

    try {
      await store.dispatch("fetchAttendanceNotifications");
    } catch (error) {
      console.error("❌ Failed to fetch notifications:", error);
    } finally {
      if (!silent) {
        isLoadingNotifications.value = false;
      }
    }
  };

  const refreshNotifications = async () => {
    await fetchNotifications();
  };

  const handleRangeChange = () => {
    currentPage.value = 1;

    if (attendanceRange.value === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      selectedDate.value = yesterday.toISOString().split("T")[0];
    }

    if (attendanceRange.value === "today") {
      selectedDate.value = new Date().toISOString().split("T")[0];
    }

    fetchAttendanceData();
  };

  // Debounced Search
  let searchTimeout;
  const debouncedSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      currentPage.value = 1;
      fetchAttendanceData();
    }, 500);
  };

  // Pagination Methods
  const previousPage = () => {
    if (currentPage.value > 1) {
      currentPage.value--;
      fetchAttendanceData();
    }
  };

  const nextPage = () => {
    if (currentPage.value < totalPages.value) {
      currentPage.value++;
      fetchAttendanceData();
    }
  };

  const goToPage = (page) => {
    currentPage.value = page;
    fetchAttendanceData();
  };

  // Utility Methods
  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "present":
        return "bg-green-100 text-green-800";
      case "late":
        return "bg-yellow-100 text-yellow-800";
      case "absent":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";

    const date = new Date(timeString);
    if (isNaN(date.getTime())) return "-";

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const calculateDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return null;

    const start = new Date(checkIn);
    const end = new Date(checkOut);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    const diffMs = end - start;
    if (diffMs <= 0) return null;

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHours}h ${diffMinutes}m`;
  };

  // Pagination Helper Methods
  const getStartRecord = () => {
    const start = (currentPage.value - 1) * Number(pageLimit.value) + 1;
    return isNaN(start) ? 1 : start;
  };

  const getEndRecord = () => {
    const end = Math.min(
      currentPage.value * Number(pageLimit.value),
      attendanceTotal.value,
    );
    return isNaN(end) ? attendanceTotal.value : end;
  };

  // Computed property to group flat records by teacher with daily attendance breakdown
  const groupedAttendanceByTeacher = computed(() => {
    const grouped = {};

    attendanceRecords.value.forEach((record) => {
      const teacherId = record.userId?._id || record._id;
      const teacherName = getTeacherName(record);

      if (!grouped[teacherId]) {
        grouped[teacherId] = {
          _id: teacherId,
          name: teacherName,
          userId: record.userId,
          photo: getTeacherPhoto(record),
          attendanceID: getTeacherId(record),
          dailyAttendance: [],
        };
      }

      // Add daily attendance record
      grouped[teacherId].dailyAttendance.push({
        date: record.date,
        status: record.status,
        checkIn: record.checkIn,
        checkOut: record.checkOut,
      });
    });

    return Object.values(grouped);
  });
  const getInitials = (name) => {
    if (!name) return "--";

    const words = name.trim().split(" ").filter(Boolean);

    if (words.length === 1) {
      // If only one word, take first 2 letters
      return words[0].slice(0, 2).toUpperCase();
    }

    const firstLetter = words[0][0];
    const lastLetter = words[words.length - 1][0];

    return `${firstLetter}${lastLetter}`.toUpperCase();
  };

  const startAutoRefresh = () => {
    attendanceInterval.value = setInterval(() => {
      if (!isTableLoading.value) {
        // 🔥 silent background refresh
        fetchAttendanceData(currentPage.value, true);
      }
    }, 5000);

    notificationInterval.value = setInterval(() => {
      if (!isLoadingNotifications.value) {
        // 🔥 silent background refresh
        fetchNotifications(true);
      }
    }, 5000);
  };
  // Lifecycle
  onMounted(() => {
    console.log("🚀 Component mounted, fetching data...");
    // Initialize custom date range for last 7 days
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    customDateFrom.value = from.toISOString().split("T")[0];
    customDateTo.value = to.toISOString().split("T")[0];

    fetchAttendanceData();
    fetchNotifications();
    startAutoRefresh();
  });
  onUnmounted(() => {
    if (attendanceInterval.value) {
      clearInterval(attendanceInterval.value);
    }

    if (notificationInterval.value) {
      clearInterval(notificationInterval.value);
    }
  });
</script>

<style scoped>
  /* Custom scrollbar for notifications */
  .max-h-80::-webkit-scrollbar {
    width: 4px;
  }

  .max-h-80::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  .max-h-80::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  .max-h-80::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
